import { useEffect, useId, useRef, useState } from "react";
import "./InventoryCountScopePicker.css";

const levels = ["families", "subfamilies", "categories", "subcategories"];
const parentKeys = [null, "family_id", "subfamily_id", "category_id"];
const messages = {
  en: { title: "Families and categories", description: "Narrow the count to product groups. Only items with stock in the selected locations will be included.",
    names: ["Families", "Subfamilies", "Categories", "Subcategories"], find: "Find", parent: "Select a group in the previous column first.",
    empty: "No matching groups.", all: "No filters: include all product groups with stock in the selected locations.",
    specific: "The most specific selected level determines the product groups included.", clear: "Clear selection", cancel: "Cancel", apply: "Apply" },
  es: { title: "Familias y categorías", description: "Limita el recuento a grupos de productos. Solo se incluirán artículos con stock en las ubicaciones seleccionadas.",
    names: ["Familias", "Subfamilias", "Categorías", "Subcategorías"], find: "Buscar", parent: "Selecciona primero un grupo en la columna anterior.",
    empty: "No hay grupos que coincidan.", all: "Sin filtros: incluir todos los grupos de productos con stock en las ubicaciones seleccionadas.",
    specific: "El nivel más específico seleccionado determina los grupos de productos incluidos.", clear: "Borrar selección", cancel: "Cancelar", apply: "Aplicar" },
  ca: { title: "Famílies i categories", description: "Limita el recompte a grups de productes. Només s'inclouran articles amb estoc a les ubicacions seleccionades.",
    names: ["Famílies", "Subfamílies", "Categories", "Subcategories"], find: "Cerca", parent: "Selecciona primer un grup a la columna anterior.",
    empty: "No hi ha grups que coincideixin.", all: "Sense filtres: incloure tots els grups de productes amb estoc a les ubicacions seleccionades.",
    specific: "El nivell més específic seleccionat determina els grups de productes inclosos.", clear: "Esborra la selecció", cancel: "Cancel·la", apply: "Aplica" },
};
const emptySelection = () => Object.fromEntries(levels.map((level) => [level, []]));

export default function InventoryCountScopePicker({ families = [], subfamilies = [], categories = [], subcategories = [], value = {}, onApply, onClose, language = "en" }) {
  const locale = String(language).toLowerCase().split(/[-_]/)[0];
  const words = messages[locale] || messages.en;
  const titleId = useId();
  const panelRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const catalog = { families, subfamilies, categories, subcategories };
  const normalize = (selection) => {
    const next = emptySelection();
    levels.forEach((level, index) => {
      const eligible = catalog[level].filter((entry) => index === 0 || next[levels[index - 1]].includes(Number(entry[parentKeys[index]])));
      const eligibleIds = new Set(eligible.map((entry) => Number(entry.id)));
      next[level] = [...new Set((selection[level] || []).map(Number))].filter((id) => eligibleIds.has(id));
    });
    return next;
  };
  const [draft, setDraft] = useState(() => normalize(value));
  const [searches, setSearches] = useState({});
  const filtered = levels.some((level) => draft[level].length);

  useEffect(() => {
    const previous = document.activeElement;
    const panel = panelRef.current;
    panel.focus({ preventScroll: true });
    const keydown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault(); event.stopImmediatePropagation(); closeRef.current();
      } else if (event.key === "Tab") {
        event.stopImmediatePropagation();
        const controls = [...panel.querySelectorAll("button:not(:disabled), input:not(:disabled)")];
        const first = controls[0]; const last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); panel.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener("keydown", keydown, true);
    return () => {
      window.removeEventListener("keydown", keydown, true);
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);

  return <div className="ic-dialogOverlay ic-scope-picker-overlay">
    <div ref={panelRef} className="ic-dialog ic-scope-picker" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
      <header><h3 id={titleId} className="ic-dialogTitle">{words.title}</h3><p>{words.description}</p></header>
      <div className="ic-scope-picker-body">
        <p role="status">{filtered ? words.specific : words.all}</p>
        <div className="ic-scope-picker-grid">{levels.map((level, index) => {
          const parents = index ? draft[levels[index - 1]] : null;
          const unavailable = index > 0 && !parents.length;
          const query = (searches[level] || "").trim().toLocaleLowerCase();
          const entries = catalog[level].filter((entry) => (!parents || parents.includes(Number(entry[parentKeys[index]]))) &&
            `${entry.name || ""} ${entry.code || ""}`.toLocaleLowerCase().includes(query));
          return <section className="ic-scope-picker-level" key={level} aria-label={words.names[index]}>
            <h4>{words.names[index]} <span>{draft[level].length}</span></h4>
            <input type="search" aria-label={`${words.find} ${words.names[index]}`} placeholder={`${words.find} ${words.names[index]}`}
              value={searches[level] || ""} disabled={unavailable} onChange={(event) => setSearches((current) => ({ ...current, [level]: event.target.value }))} />
            <div className="ic-scope-picker-options">
              {unavailable ? <p>{words.parent}</p> : entries.length ? entries.map((entry) => {
                const id = Number(entry.id); const selected = draft[level].includes(id);
                return <button type="button" key={id} className={`ic-scope-picker-choice${selected ? " ic-scope-picker-choice--selected" : ""}`}
                  aria-pressed={selected} title={[entry.name, entry.code].filter(Boolean).join(" · ")}
                  onClick={() => setDraft((current) => normalize({ ...current, [level]: selected ? current[level].filter((value) => value !== id) : [...current[level], id] }))}>
                  <strong>{entry.name}</strong>{entry.code && <small>{entry.code}</small>}
                </button>;
              }) : <p>{words.empty}</p>}
            </div>
          </section>;
        })}</div>
      </div>
      <footer className="ic-dialogActions">
        <button type="button" className="ic-btn ic-btn--ghost" disabled={!filtered} onClick={() => { setDraft(emptySelection()); setSearches({}); }}>{words.clear}</button>
        <button type="button" className="ic-btn ic-btn--ghost" onClick={onClose}>{words.cancel}</button>
        <button type="button" className="ic-btn ic-btn--primary" onClick={() => onApply(normalize(draft))}>{words.apply}</button>
      </footer>
    </div>
  </div>;
}
